import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent, Button } from '../components/ui';
import { api } from '../lib/api';
import type { CrmDealType, PromotionProposal, PromotionResult } from '../lib/api';

interface Opp {
  id: string; name: string; pipeline_name: string; stage_id: string; stage_name: string;
  monetary_value: number | null; status: string | null; assigned_to: string | null;
  contact_name: string | null; contact_apex_id: string | null; last_synced_at: string | null;
  raw_data: any; valid_stages: Array<{ id: string; name: string }>;
}

/** Every deal type `crm_deals` accepts, in the order the picker offers them. */
const DEAL_TYPES: CrmDealType[] = ['sale', 'lease', 'listing', 'acquisition', 'disposition'];

export default function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [opp, setOpp] = useState<Opp | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [form, setForm] = useState({ name: '', monetaryValue: '', status: '', pipelineStageId: '' });

  // --- D26: promotion to a crm_deals row, so the AIR contract builder has something to read.
  const [proposal, setProposal] = useState<PromotionProposal | null>(null);
  const [proposalLoading, setProposalLoading] = useState(true);
  const [proposalError, setProposalError] = useState<string | null>(null);
  const [dealType, setDealType] = useState<CrmDealType | ''>('');
  const [promoting, setPromoting] = useState(false);
  const [promoteError, setPromoteError] = useState<string | null>(null);
  const [promoted, setPromoted] = useState<PromotionResult | null>(null);
  /**
   * IN-FLIGHT GUARD. A ref, not the `promoting` state, and not only the button's `disabled`:
   * both of those depend on a re-render having happened, and two clicks can land inside one
   * React batch. A double-click here previously filed TWO contracts elsewhere in this project.
   */
  const promotingRef = useRef(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await api.getOpportunity(id);
      const o: Opp = res.opportunity;
      setOpp(o);
      setForm({
        name: o.name || '', monetaryValue: o.monetary_value == null ? '' : String(o.monetary_value),
        status: (o.status || '').toLowerCase(), pipelineStageId: o.stage_id || '',
      });
    } finally { setLoading(false); }
  }, [id]);

  /**
   * Deliberately its OWN fetch with its own error state, not folded into `load()`: if the
   * promotion endpoint is down, the opportunity itself must still render and still be
   * editable. A failure here costs the contract affordance and nothing else.
   */
  const loadProposal = useCallback(async () => {
    if (!id) return;
    setProposalLoading(true);
    setProposalError(null);
    try {
      const res = await api.getPromotionProposal(id);
      setProposal(res.data);
      // Preselect ONLY what the server actually proposed. `?? ''` leaves the picker empty when
      // it proposed nothing - never a fallback, because the deal type decides which AIR form
      // is built and that is Joseph's judgement, not a default.
      setDealType(res.data.proposedDealType ?? '');
    } catch (e: any) {
      setProposal(null);
      setProposalError(e?.message || 'Could not check whether this opportunity is a deal yet');
    } finally {
      setProposalLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadProposal(); }, [loadProposal]);

  const promote = async () => {
    if (!id) return;
    // Re-entry is ignored outright rather than queued.
    if (promotingRef.current) return;
    // No deal type, no request. The button is disabled too, but a handler that can create a
    // legal-document record must not rely on the disabled attribute alone.
    if (!dealType) return;

    promotingRef.current = true;
    setPromoting(true);
    // Clearing here also satisfies "clear any stale error on a subsequent success": the banner
    // from the previous attempt is gone before the new one resolves either way.
    setPromoteError(null);
    try {
      const res = await api.promoteOpportunity(id, dealType);
      setPromoted(res.data);
    } catch (e: any) {
      // Catches BOTH an APIError (server said no, message is the server's) and a raw network
      // throw. Without this the rejection would be unhandled and the screen would sit there
      // looking like the deal had been created.
      setPromoteError(e?.message || 'Could not create the deal');
    } finally {
      promotingRef.current = false;
      setPromoting(false);
    }
  };

  const save = async () => {
    if (!id) return;
    setSaving(true); setMsg(null);
    try {
      const body: Record<string, unknown> = {
        name: form.name,
        monetaryValue: form.monetaryValue === '' ? undefined : Number(form.monetaryValue),
        status: form.status,
        pipelineStageId: form.pipelineStageId || undefined,
      };
      const res = await api.updateOpportunity(id, body);
      setOpp(res.opportunity);
      setMsg({ type: 'ok', text: 'Saved to GoHighLevel' });
    } catch (e: any) {
      setMsg({ type: 'err', text: e?.message || 'Save failed' });
    } finally { setSaving(false); }
  };

  if (loading) return <Layout><div className="text-center py-12 text-gray-500">Loading...</div></Layout>;
  if (!opp) return <Layout><div className="text-center py-12 text-gray-500">Opportunity not found</div></Layout>;

  const raw = opp.raw_data || {};
  const fields: Array<[string, any]> = [
    ['Pipeline', opp.pipeline_name],
    ['Source', raw.source],
    ['Created', raw.createdAt], ['Updated', raw.updatedAt],
    ['Last stage change', raw.lastStageChangeAt], ['Last status change', raw.lastStatusChangeAt],
    ['Assigned to', opp.assigned_to],
  ];

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <button onClick={() => navigate('/crm/opportunities')} className="text-sm text-gray-500 hover:text-primary-600">← Opportunities</button>
        <div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <h1 className="font-display text-[32px] leading-[1.06] font-semibold uppercase tracking-[-.01em] text-gray-900">{opp.name || 'Opportunity'}</h1>
            {opp.status && (
              <span className={`capitalize text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${
                opp.status.toLowerCase() === 'won' ? 'bg-success-wash text-success'
                  : opp.status.toLowerCase() === 'lost' ? 'bg-gray-100 text-gray-500'
                  : 'bg-primary-50 text-primary-500'
              }`}>{opp.status}</span>
            )}
          </div>
          <p className="text-gray-500 text-sm mt-1">
            {opp.pipeline_name}{opp.stage_name ? ` · ${opp.stage_name}` : ''}
            {opp.monetary_value != null ? ` · $${Number(opp.monetary_value).toLocaleString()}` : ''}
          </p>
        </div>

        {msg && (
          <div className={`rounded-lg border p-3 text-sm ${msg.type === 'ok' ? 'bg-success-wash border-success/30 text-success' : 'bg-red-50 border-red-200 text-red-700'}`}>{msg.text}</div>
        )}

        {/* Editable */}
        <Card>
          <CardHeader>
            <CardTitle>Opportunity</CardTitle>
            <p className="text-xs text-gray-400 mt-0.5">Edits save straight to GoHighLevel.</p>
          </CardHeader>
          <CardContent className="space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Name</label>
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Value (USD)</label>
              <input type="number" min="0" value={form.monetaryValue} onChange={(e) => setForm((f) => ({ ...f, monetaryValue: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                {['open', 'won', 'lost', 'abandoned'].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Stage</label>
            <select value={form.pipelineStageId} onChange={(e) => setForm((f) => ({ ...f, pipelineStageId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              {opp.valid_stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save to GoHighLevel'}</Button>
          </CardContent>
        </Card>

        {/* Contract - D26. The AIR contract builder reads crm_deals; opportunities are not
            deals, so one has to be created before a contract can be built for this one. */}
        <Card>
          <CardHeader>
            <CardTitle>Contract</CardTitle>
            <p className="text-xs text-gray-400 mt-0.5">A contract is built from a deal, not from an opportunity.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {proposalLoading && <p className="text-sm text-gray-500">Checking…</p>}

            {!proposalLoading && proposalError && (
              <div className="space-y-2">
                <div role="alert" className="rounded-lg border border-warning/30 bg-warning-wash p-3 text-sm text-warning">
                  {proposalError}
                </div>
                {/* No promote control while this is unknown: without the answer we cannot tell
                    a not-yet-promoted opportunity from one that already has a deal. */}
                <Button variant="outline" size="sm" onClick={loadProposal}>Try again</Button>
              </div>
            )}

            {!proposalLoading && !proposalError && proposal && (
              promoted ? (
                <div className="space-y-2">
                  <div role="status" className="rounded-lg border border-success/30 bg-success-wash p-3 text-sm text-success">
                    {promoted.created
                      ? `Deal created for this opportunity as a ${promoted.dealType}.`
                      : `This opportunity was already linked to a ${promoted.dealType} deal, so nothing new was created.`}
                  </div>
                  <Link to={`/contracts/${promoted.dealId}`} className="text-primary-500 hover:underline font-medium text-sm">
                    Open the contract builder
                  </Link>
                </div>
              ) : proposal.alreadyLinkedDealId ? (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">This opportunity is already a deal.</p>
                  <Link to={`/contracts/${proposal.alreadyLinkedDealId}`} className="text-primary-500 hover:underline font-medium text-sm">
                    Open the contract builder
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label htmlFor="promote-deal-type" className="block text-xs text-gray-500 mb-1">Deal type</label>
                    <select
                      id="promote-deal-type"
                      value={dealType}
                      onChange={(e) => setDealType(e.target.value as CrmDealType | '')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">Choose a deal type…</option>
                      {DEAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {proposal.proposedDealType
                        ? (proposal.prefix
                            ? `Proposed from the "${proposal.prefix}" prefix in the name - confirm or change.`
                            : 'Proposed from the opportunity name - confirm or change.')
                        : 'Nothing in the name says which type this is. The deal type decides which AIR form is used, so pick it.'}
                    </p>
                  </div>

                  {promoteError && (
                    <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                      {promoteError}
                    </div>
                  )}

                  <Button onClick={promote} disabled={!dealType || promoting}>
                    {promoting ? 'Creating…' : 'Create deal'}
                  </Button>
                </div>
              )
            )}
          </CardContent>
        </Card>

        {/* Linked contact */}
        {opp.contact_apex_id && (
          <Card><CardContent>
            <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-1">Linked contact</p>
            <Link to={`/crm/contacts/${opp.contact_apex_id}`} className="text-primary-500 hover:underline font-medium">{opp.contact_name || 'View contact'}</Link>
          </CardContent></Card>
        )}

        {/* Read-only details */}
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
            <p className="text-xs text-gray-400 mt-0.5">Read-only, from GoHighLevel.</p>
          </CardHeader>
          <CardContent>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
            {fields.filter(([, v]) => v != null && v !== '').map(([label, v]) => (
              <div key={label}>
                <dt className="text-[11px] uppercase tracking-wider text-gray-400">{label}</dt>
                <dd className="text-gray-900 font-medium mt-1">{String(v)}</dd>
              </div>
            ))}
          </dl>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
